import React, { FC, useEffect, useRef } from 'react';
import { BsX } from 'react-icons/bs';
import { FaArchive } from 'react-icons/fa';
import NavDropdown from 'react-bootstrap/NavDropdown';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';

import styled from 'styled-components';

import { PermissionKeys } from 'store/types';
import { useActiveClientContext } from 'store/active-client';
import { useWizardContext } from 'store/wizard';
import { usePermissionsContext } from 'store/permissions';

import { getSubdomain, isActiveRoute } from 'utils/helpers/routing';
import { buildClientUrl } from 'utils/helpers/client';
import useArchiveData from 'utils/helpers/useArchiveData';

import LanguageSwitch from 'components/LanguageSwitch';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { IHeaderProps } from './types';

interface IHeaderMobileProps extends IHeaderProps {
  isVisible: boolean;
  onClose: () => void;
}

const HeaderMobile: FC<IHeaderMobileProps> = ({
  menuItems,
  isVisible,
  onClose,
  showLangSwitch,
  isClientAdmin,
}: IHeaderMobileProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const { client } = useActiveClientContext();
  const { checkPermission } = usePermissionsContext();
  const { resetWizardState } = useWizardContext();

  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const languageSwitcherRef = useRef<HTMLDivElement>(null);

  const archiveData = useArchiveData();

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (
      mobileMenuRef.current &&
      mobileMenuRef.current.contains(target) &&
      (!languageSwitcherRef.current || !languageSwitcherRef.current.contains(target))
    ) {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const renderMobileNav = () => {
    const isOperator = getSubdomain() === 'operators';
    /**
     * !!!!!!!!!!!!!!!!!!!!!!!!
     *
     * If updating mobile nav here, please make sure
     * that you update header navigation as well
     *
     * !!!!!!!!!!!!!!!!!!!!!!!!
     */
    return menuItems.map((menuItem, index) => {
      if (menuItem.childrenRoutes && menuItem.childrenRoutes.length) {
        /**
         * If the operator is in client that doesn't have the
         * templates activated - don't show the settings menu
         */
        if (isOperator && menuItem.to === buildClientUrl('/settings', client) && !client?.templates) return null;
        return (
          <MobileNavDropdown
            key={menuItem.label}
            title={
              <span className="text-light">
                <menuItem.icon
                  size={20}
                  style={{ marginRight: '0.5rem' }}
                />
                {t(menuItem.label)}
              </span>
            }
            id={`nav-dropdown-${index}`}
          >
            {menuItem.childrenRoutes.map(submenuItem => (
              <StyledNavLink
                key={submenuItem.label}
                isActive={isActiveRoute(location, submenuItem.to, client)}
              >
                <Link
                  to={submenuItem.to}
                  className="nav-link pl-4 text-light"
                  onClick={onClose}
                >
                  <submenuItem.icon
                    size={20}
                    style={{ marginRight: '0.5rem' }}
                  />
                  {t(submenuItem.label)}
                </Link>
              </StyledNavLink>
            ))}
          </MobileNavDropdown>
        );
      }

      if (menuItem.type === 'button') {
        const canCreateDoc = !isOperator && checkPermission(PermissionKeys.CAN_CREATE_DOCUMENTS);
        return (
          <TooltipWrapper
            key={menuItem.label}
            id="tooltip-create-doc-mobile"
            overlay={t(isOperator ? 'docWizard.operatorMessage' : 'common.noPermission')}
            placement="bottom"
            show={canCreateDoc ? false : undefined}
            wrapperClass="d-flex"
          >
            <Button
              className="rounded-0 d-flex"
              style={{ width: '100%' }}
              onClick={() => {
                // Reset doc wizard state
                resetWizardState();
                history.push({
                  pathname: menuItem.to,
                  state: {
                    ...(menuItem.to === '/documents-wizard/step-1' ? { isNew: 1 } : {}),
                  },
                });
              }}
              disabled={!canCreateDoc}
            >
              <menuItem.icon
                size={20}
                style={{ marginRight: '0.5rem' }}
              />
              {t(menuItem.label)}
            </Button>
          </TooltipWrapper>
        );
      }
      return (
        <StyledNavLink
          key={menuItem.label}
          isActive={isActiveRoute(location, menuItem.to, client)}
        >
          <Link
            to={menuItem.to}
            className="text-light nav-link"
            onClick={onClose}
          >
            <menuItem.icon
              size={20}
              style={{ marginRight: '0.5rem' }}
            />
            {t(menuItem.label)}
          </Link>
        </StyledNavLink>
      );
    });
  };

  return (
    <MobileOverlay
      aria-label="mobile-menu-close"
      className="d-lg-none"
      ref={mobileMenuRef}
      visible={!!isVisible}
    >
      <MobileMenu visible={!!isVisible}>
        <TopBar>
          <CloseBtn
            className="d-lg-none d-block text-light"
            onClick={onClose}
          >
            <BsX size={25} />
          </CloseBtn>
          <LanguageSwitchWrapper ref={languageSwitcherRef}>
            {process.env.REACT_APP_SHOW_LANG_MENU === 'true' && showLangSwitch && (
              <LanguageSwitch
                theme="light"
                onSelect={onClose}
              />
            )}
          </LanguageSwitchWrapper>
        </TopBar>
        {renderMobileNav()}
        {(() => {
          const { hasAvailableArchives, availableUndownloadedArchivesCount } = archiveData;
          if (isClientAdmin && hasAvailableArchives) {
            return (
              <MobileArchiveLink
                isActive={location.pathname.includes('/archives')}
                onClick={() => {
                  history.push(client?.id ? `/client/${client.id}/archives` : '/archives');
                  onClose();
                }}
              >
                <FaArchive
                  size={20}
                  style={{ marginRight: '0.5rem' }}
                />
                {t('archives.title')}
                {availableUndownloadedArchivesCount !== undefined && availableUndownloadedArchivesCount > 0 && (
                  <MobileNotificationBadge>{availableUndownloadedArchivesCount}</MobileNotificationBadge>
                )}
              </MobileArchiveLink>
            );
          }
          return null;
        })()}
        <Button
          className="logout-btn pl-3 pr-3 mt-3 ml-3"
          btnType="primary"
          size="sm"
          outlined
          onClick={() => history.push('/logout')}
        >
          {t('menu.logout')}
        </Button>
      </MobileMenu>
    </MobileOverlay>
  );
};

export default HeaderMobile;

const MobileOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  background: rgba(0, 0, 0, 0.4);
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  opacity: ${props => (props.visible ? 1 : 0)};
  visibility: ${props => (props.visible ? 'visible' : 'hidden')};
  transition: 0.4s ease;
  z-index: 100;
`;

const MobileMenu = styled.div<{ visible: boolean }>`
  padding: 1rem 0;
  width: 260px;
  background: ${props => props.theme.colors.darker};
  height: 100%;
  transform: translateX(${props => (props.visible ? 0 : '260px')});
  transition: 0.4s ease;
`;

const MobileNavDropdown = styled(NavDropdown)`
  .dropdown-menu {
    position: relative;
    border: 0;
    border-radius: 0;
    background: none;
  }
`;

const StyledNavLink = styled.span<{ isActive: boolean }>`
  a {
    color: ${props => (props.isActive ? props.theme.colors.violet : props.theme.colors.white)} !important;

    &:hover {
      color: ${props => props.theme.colors.violet} !important;
    }
  }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  padding: 0.25rem 0.75rem;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.5rem;
`;

const LanguageSwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 0.5rem;
`;

const MobileArchiveLink = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  padding: 0.5rem 1rem;
  margin: 0.5rem 0;
  color: ${props => (props.isActive ? props.theme.colors.violet : props.theme.colors.white)};
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${props => props.theme.colors.violet};
  }
`;

const MobileNotificationBadge = styled.span`
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  margin-left: 0.5rem;
`;
